/**
 * @file きき分けあそびの StudyProgressLog.detail (機械可読 JSON) の型と parse/serialize。
 * @why: detail の語彙はサーバでなく web 側が持つ (epic_24 §6.2。サーバは valid JSON と
 *   4096 バイトのみ検証)。parse は構造を検証し、壊れていれば null を返してエンジン側が
 *   フルリプレイへ落ちる (epic_24 §6.4 の自己修復)。
 * @failure: parse は例外を投げない (壊れた detail は null)。after の構造が不正な場合も
 *   trials だけ生かす (リプレイの材料にはなる)。
 */

import { isChordId, type ChordId } from "./chords";

/** 原典の教材 id (prod 実在確認済み 2026-08-25)。記録の紐づけと判別の第 2 手掛かりに使う。 */
export const ONKAN_MATERIAL_ID = "book-eguchi-onkan";

/**
 * きき分けあそびのログかを判別する (一覧の除外・集計用)。
 * detail の kind が第 1 手掛かり (文字列の前置き判定で絞ってから JSON parse で確定し、
 * 他フィールドに同じ文字列が紛れても誤判定しない)。backend が detail 未対応のまま
 * 書かれた過去ログ向けに material_id を第 2 手掛かりにする (epic_24 §4.4)。
 */
export function looksLikeKikiwakeLog(log: { detail?: string; material_id?: string }): boolean {
  if (log.material_id === ONKAN_MATERIAL_ID) return true;
  const d = log.detail;
  if (d === undefined || !d.includes("kikiwake")) return false;
  try {
    const o: unknown = JSON.parse(d);
    return typeof o === "object" && o !== null && (o as { kind?: unknown }).kind === "kikiwake";
  } catch {
    return false;
  }
}

/** 1 出題の記録。x = 押した和音 (正解時は省略)。n = ノーカウント (§5.2)。 */
export interface KikiwakeTrial {
  c: ChordId;
  x?: ChordId;
  /** もう一度きいた回数 (0 は省略)。v1 の判定には使わない (後日の分析用)。 */
  r?: number;
  n?: boolean;
  /** 再生完了から回答までの ms。 */
  ms?: number;
  /** かため弾き枠 (治療中の最後の集中出題)。混ぜる練習でないため進段判定から除く。 */
  k?: boolean;
}

export interface KikiwakeCare {
  /** 混ぜる練習から外している和音 (s.122 の 3 ケースで決める)。 */
  isolated: ChordId;
  /** まちがいあう相手 (混ぜる練習に残す方)。 */
  mixed: ChordId;
  kind: "equal" | "asymmetric" | "one_way";
  /** 復帰判定用: isolated をミスなしでこなした実施日 (連続でないとリセット)。 */
  cleanDates: string[];
}

export interface KikiwakeState {
  /** 現在の和音セット。常に KIKIWAKE_CHORDS の先頭からの部分列 (追加順厳守 R4)。 */
  active: ChordId[];
  addedAt: Partial<Record<ChordId, string>>;
  /** 最後の「カウント対象」ミスの日 (R19 でノーカウントは除く)。 */
  lastMissDate: string | null;
  care: KikiwakeCare | null;
  /** 直近 30 日の日別 回答数 t / カウント対象ミス数 m (実施日・連続日数の判定材料)。 */
  recentDays: Record<string, { t: number; m: number }>;
  /** 直近 careWindowPracticeDays 実施日のカウント対象誤答 (c=出題, x=回答)。 */
  recentMissEvents: { d: string; c: ChordId; x: ChordId }[];
  /** 前セッションの先頭和音 (R15: 毎回同じ和音から始めない)。 */
  lastFirstChord: ChordId | null;
}

export interface KikiwakeDetail {
  v: 1;
  kind: "kikiwake";
  /** 使った運用値の版 (kikiwakeConfig.ts)。リプレイはこの版で再生する。 */
  cfg: number;
  /** セッション通し番号 (単調増加)。系譜検査 (§6.4) 用。 */
  seq: number;
  /** 起点にした直前 kikiwake ログの id (初回は null)。系譜検査用。 */
  base: string | null;
  /** 途中離脱保存。trial は等しく判定に使い、実施日は日合計で数える (§4.1)。 */
  partial?: true;
  /** 無音疑い (全問誤答 + 平均反応が速すぎ)。trial は全て n 済み (§5.2)。 */
  muted_suspect?: true;
  trials: KikiwakeTrial[];
  /** セッション適用後の状態チェックポイント。 */
  after: KikiwakeState;
}

function isDate(v: unknown): v is string {
  return typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v);
}

function parseTrial(raw: unknown): KikiwakeTrial | null {
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  if (!isChordId(o.c)) return null;
  const t: KikiwakeTrial = { c: o.c };
  if (o.x !== undefined) {
    if (!isChordId(o.x)) return null;
    t.x = o.x;
  }
  if (typeof o.r === "number" && o.r > 0) t.r = o.r;
  if (o.n === true) t.n = true;
  if (typeof o.ms === "number" && o.ms >= 0) t.ms = o.ms;
  if (o.k === true) t.k = true;
  return t;
}

/** active が正典順の先頭からの部分列かつ addedAt が揃っているかまで見る (エンジンの不変条件)。 */
export function parseKikiwakeState(raw: unknown, canonOrder: readonly ChordId[]): KikiwakeState | null {
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  if (!Array.isArray(o.active) || o.active.length === 0 || o.active.length > canonOrder.length) return null;
  for (let i = 0; i < o.active.length; i++) {
    if (o.active[i] !== canonOrder[i]) return null;
  }
  const active = o.active as ChordId[];
  if (typeof o.addedAt !== "object" || o.addedAt === null) return null;
  const addedAtRaw = o.addedAt as Record<string, unknown>;
  const addedAt: Partial<Record<ChordId, string>> = {};
  for (const id of active) {
    if (!isDate(addedAtRaw[id])) return null;
    addedAt[id] = addedAtRaw[id] as string;
  }
  const lastMissDate = isDate(o.lastMissDate) ? o.lastMissDate : null;
  let care: KikiwakeCare | null = null;
  if (typeof o.care === "object" && o.care !== null) {
    const c = o.care as Record<string, unknown>;
    if (
      isChordId(c.isolated) &&
      isChordId(c.mixed) &&
      (c.kind === "equal" || c.kind === "asymmetric" || c.kind === "one_way") &&
      Array.isArray(c.cleanDates) &&
      c.cleanDates.every(isDate)
    ) {
      care = { isolated: c.isolated, mixed: c.mixed, kind: c.kind, cleanDates: c.cleanDates as string[] };
    } else {
      return null;
    }
  }
  const recentDays: Record<string, { t: number; m: number }> = {};
  if (typeof o.recentDays === "object" && o.recentDays !== null) {
    for (const [d, v] of Object.entries(o.recentDays as Record<string, unknown>)) {
      if (!isDate(d) || typeof v !== "object" || v === null) return null;
      const e = v as Record<string, unknown>;
      if (typeof e.t !== "number" || typeof e.m !== "number") return null;
      recentDays[d] = { t: e.t, m: e.m };
    }
  }
  const recentMissEvents: KikiwakeState["recentMissEvents"] = [];
  if (Array.isArray(o.recentMissEvents)) {
    for (const ev of o.recentMissEvents) {
      const e = ev as Record<string, unknown>;
      if (!isDate(e.d) || !isChordId(e.c) || !isChordId(e.x)) return null;
      recentMissEvents.push({ d: e.d, c: e.c, x: e.x });
    }
  }
  const lastFirstChord = isChordId(o.lastFirstChord) ? o.lastFirstChord : null;
  return { active, addedAt, lastMissDate, care, recentDays, recentMissEvents, lastFirstChord };
}

/**
 * detail 文字列を parse する。kind が kikiwake でなければ null (他機能の detail は無視)。
 * after が壊れている場合は after を initialState 相当に落とさず null を返し、
 * 呼び出し側 (resolveKikiwakeState) がリプレイで復元する。
 */
export function parseKikiwakeDetail(
  raw: string | undefined,
  canonOrder: readonly ChordId[],
): { detail: Omit<KikiwakeDetail, "after">; after: KikiwakeState | null } | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const o = parsed as Record<string, unknown>;
  if (o.kind !== "kikiwake" || o.v !== 1) return null;
  if (typeof o.cfg !== "number" || typeof o.seq !== "number") return null;
  const base = typeof o.base === "string" ? o.base : null;
  if (!Array.isArray(o.trials)) return null;
  const trials: KikiwakeTrial[] = [];
  for (const t of o.trials) {
    const p = parseTrial(t);
    if (!p) return null;
    trials.push(p);
  }
  const detail: Omit<KikiwakeDetail, "after"> = { v: 1, kind: "kikiwake", cfg: o.cfg, seq: o.seq, base, trials };
  if (o.partial === true) detail.partial = true;
  if (o.muted_suspect === true) detail.muted_suspect = true;
  return { detail, after: parseKikiwakeState(o.after, canonOrder) };
}

export function serializeKikiwakeDetail(d: KikiwakeDetail): string {
  return JSON.stringify(d);
}
